import type { AnalysisEvent } from '../api/client';
import { useProcessingStore } from '../stores/useProcessingStore';
import { ANALYSIS_STAGE_COUNT, isRefineStage } from '../data/processing';
import { formatElapsedSeconds, humanizeToolArgs, humanizeToolName, tallyToolResult } from './feedSummarizer';

/**
 * Replay persisted analysis events into the processing store to reconstruct
 * UI state after a page reload.
 *
 * Events are replayed synchronously (no animation/throttle) — the store
 * updates in bulk and React renders the final state in one pass.
 */
export function replayAnalysisEvents(
  events: AnalysisEvent[],
  startTimeMs?: number,
): {
  lastSeq: number;
  isComplete: boolean;
  hasError: boolean;
} {
  let lastSeq = 0;
  let isComplete = false;
  let hasError = false;

  const counters = { dataPoints: 0, insights: 0, recommendations: 0 };
  const feedMessages: { time: string; msg: string; type: string }[] = [];

  // Track stage state for bulk update
  const stageUpdates: Map<number, 'active' | 'completed'> = new Map();
  const stageProgress: Map<number, number> = new Map();
  // Subtask label/count per stage, reconstructed from the tool_start stream
  const stageSubtaskLabel: Map<number, string> = new Map();
  const stageSubtaskCount: Map<number, number> = new Map();
  let activeStageNum = 0;

  // Dedup consecutive repeated tool events (e.g. load_raw_data called 10+ times)
  const toolStartCounts: Map<string, number> = new Map();
  const toolResultCounts: Map<string, number> = new Map();

  // Elapsed-time baseline: use the caller's start time if provided (so replay
  // and live stream share the exact same zero-point), else fall back to the
  // first event's created_at.
  const baselineMs = startTimeMs ?? (events[0] ? new Date(events[0].created_at).getTime() : Date.now());
  const formatTime = (iso: string): string => {
    try {
      const ms = new Date(iso).getTime() - baselineMs;
      return formatElapsedSeconds(ms / 1000);
    } catch {
      return '';
    }
  };

  for (const ev of events) {
    lastSeq = ev.seq;
    const p = ev.payload;

    switch (ev.event_type) {
      case 'stage_started': {
        const wireNum = (p.stage_number as number) ?? 0;
        // Refine-pass events restart at stage_number 1 with total_stages=4 \u2014
        // continue the combined list after the 7 analysis stages.
        const refine = isRefineStage(
          (p.stage_name as string) || undefined,
          typeof p.total_stages === 'number' ? p.total_stages : undefined,
        );
        const stageNum = refine ? ANALYSIS_STAGE_COUNT + wireNum : wireNum;
        // Mark all prior stages as completed
        for (let i = 1; i < stageNum; i++) {
          stageUpdates.set(i, 'completed');
          stageProgress.set(i, 100);
        }
        stageUpdates.set(stageNum, 'active');
        activeStageNum = stageNum;
        // Reset per-stage dedup + subtask counters
        toolStartCounts.clear();
        toolResultCounts.clear();
        stageSubtaskLabel.delete(stageNum);
        stageSubtaskCount.set(stageNum, 0);
        feedMessages.push({
          time: formatTime(ev.created_at),
          msg: refine ? (p.stage_name as string) : `Stage ${wireNum}: ${p.stage_name as string}`,
          type: 'highlight',
        });
        break;
      }
      case 'stage_completed': {
        const wireNum = (p.stage_number as number) ?? 0;
        const refine = isRefineStage((p.stage_name as string) || undefined);
        const stageNum = refine ? ANALYSIS_STAGE_COUNT + wireNum : wireNum;
        stageUpdates.set(stageNum, 'completed');
        stageProgress.set(stageNum, 100);
        stageSubtaskLabel.delete(stageNum);
        feedMessages.push({
          time: formatTime(ev.created_at),
          msg: `\u2713 ${p.stage_name as string} complete`,
          type: 'highlight',
        });
        break;
      }
      case 'tool_start': {
        const name = p.name as string;
        const input = p.input as Record<string, unknown> | undefined;
        const dedupKey = `${name}::${input ? JSON.stringify(input) : ''}`;
        const count = (toolStartCounts.get(dedupKey) ?? 0) + 1;
        toolStartCounts.set(dedupKey, count);
        const detail = humanizeToolArgs(name, input);
        const label = humanizeToolName(name);
        // Track subtask state for whichever stage is currently active
        if (activeStageNum > 0) {
          stageSubtaskCount.set(activeStageNum, (stageSubtaskCount.get(activeStageNum) ?? 0) + 1);
          stageSubtaskLabel.set(activeStageNum, detail ? `${label} · ${detail}` : label);
        }
        // Only emit first occurrence of each (name, args) pair to the feed
        if (count === 1) {
          feedMessages.push({
            time: formatTime(ev.created_at),
            msg: detail ? `${label} — ${detail}` : `${label} starting...`,
            type: '',
          });
        }
        break;
      }
      case 'tool_result': {
        const name = p.name as string;
        const isError = p.is_error as boolean;
        const delta = tallyToolResult(name, (p.result as string) ?? '', isError);
        counters.dataPoints += delta.dataPoints ?? 0;
        counters.insights += delta.insights ?? 0;
        counters.recommendations += delta.recommendations ?? 0;
        const count = (toolResultCounts.get(name) ?? 0) + 1;
        toolResultCounts.set(name, count);
        // Only show first occurrence; skip duplicates
        if (count === 1) {
          feedMessages.push({
            time: formatTime(ev.created_at),
            msg: isError ? `Error: ${humanizeToolName(name)}` : `${humanizeToolName(name)} complete`,
            type: isError ? 'warn' : '',
          });
        }
        break;
      }
      case 'done':
        isComplete = true;
        feedMessages.push({
          time: formatTime(ev.created_at),
          msg: 'Analysis complete',
          type: 'highlight',
        });
        break;
      case 'error':
        hasError = true;
        feedMessages.push({
          time: formatTime(ev.created_at),
          msg: `Error: ${p.message as string}`,
          type: 'alert',
        });
        break;
    }
  }

  // A trailing `done` is not success: the backend agent loop emits it even when
  // the run ended in a terminal error, so a failed refine replays as
  // error-then-done. Treat an errored run as incomplete, otherwise the restore
  // path navigates the user to a report that was never compiled.
  //
  // Unlike the backend's `_analysis_is_complete` (where a real deliverable wins
  // over an earlier error), this guard has NO deliverable escape hatch — an
  // error always wins here, even if a deliverable was in fact produced before
  // the error. That is only correct because every current caller checks a
  // deliverable or `is_complete` BEFORE invoking replay; if a future caller
  // relies on this function's `isComplete` as the sole completion signal
  // without that pre-check, it will wrongly treat a deliverable-bearing run as
  // incomplete. Keep that invariant in mind before reusing this return value.
  if (hasError) {
    isComplete = false;
  }

  // Apply all state updates to the store in one pass
  useProcessingStore.setState((state) => {
    const stages = state.stages.map((s, i) => {
      const stageNum = i + 1; // stages are 1-indexed in events
      const status = stageUpdates.get(stageNum);
      const progress = stageProgress.get(stageNum);
      const resolvedStatus = status ?? s.status;
      return {
        ...s,
        status: resolvedStatus,
        progress: progress ?? s.progress,
        // Only keep subtask hint while a stage is still active on the resumed UI
        subtaskLabel: resolvedStatus === 'active' ? stageSubtaskLabel.get(stageNum) : undefined,
        subtaskCount: stageSubtaskCount.get(stageNum) ?? s.subtaskCount,
      };
    });

    return {
      stages,
      feedMessages: feedMessages.slice(-200),
      counters,
      isComplete,
    };
  });

  return { lastSeq, isComplete, hasError };
}
